/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { graphql } from 'react-apollo';

import { compose, setDisplayName } from 'recompose';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import connectToRouter from 'js/lib/connectToRouter';
import { apolloFetchPolicy } from 'js/utils/apolloFetchPolicy';

import { Grid, Typography } from '@coursera/cds-core';

import { domainBackgroundScoreMap } from 'bundles/browse-common/constants';
import TopicCard from 'bundles/browse-common/legacy/components/Domain/TopicCard';
import type { Subdomain } from 'bundles/browse-common/legacy/components/Domain/types/SubDomain';
import { DomainGetAllQuery } from 'bundles/browse-common/legacy/queries/DomainGetAll';

import _t from 'i18n!nls/browse';

type Topic = {
  id: string;
  slug: string | null;
  name: string;
};

type Props = {
  topicsList: Array<Topic>;
  router: InjectedRouter;
};

const style = css`
  margin: 0 auto 75px;
  max-width: 885px;

  .other-topics-header {
    font-family: 'Source Sans Pro', Arial, sans-serif;
    font-weight: 600;
    margin-bottom: 1.25rem;
  }

  .topic-card-grid {
    background-color: var(--cds-color-neutral-background-primary);
    margin-bottom: 16px;
    margin-right: 28px;
    padding: 0;
    height: 74px;
    max-width: 200px;

    &:nth-of-type(4n) {
      margin-right: 0;
    }
  }

  @media (max-width: 915px) {
    .topic-card-grid {
      &:nth-of-type(4n) {
        margin-right: 28px;
      }

      &:nth-of-type(3n) {
        margin-right: 0;
      }
    }
  }

  @media (max-width: 687px) {
    .topic-card-grid {
      margin-right: 8px;
      margin-bottom: 8px;

      &:nth-of-type(4n),
      &:nth-of-type(3n) {
        margin-right: 8px;
      }
    }
  }

  @media (max-width: 599px) {
    .topic-card-grid {
      max-width: 140px;
      height: 88px;
    }
  }
`;

export class OtherTopics extends React.PureComponent<Props> {
  render() {
    const {
      topicsList,
      router: {
        params: { domainSlug },
      },
    } = this.props;
    if (topicsList.length < 1) {
      return null; // just don't render if api fails
    }

    const getTopicsToShow = () => {
      return topicsList.filter((topic) => {
        // Condition is necessary because health domain still goes by its old name life-sciences which is different from the topic Id
        return topic.slug ? topic.slug !== domainSlug : topic.id !== domainSlug;
      });
    };

    const renderTopicCard = (topic: Topic) => {
      const topicCardProps = {
        ...topic,
        // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        imageUrl: domainBackgroundScoreMap[topic.id].url,
      };

      return <TopicCard {...topicCardProps} />;
    };

    const renderTopicCardList = () => {
      return getTopicsToShow().map((topic) => (
        <Grid className="topic-card-grid" item key={topic.id} lg={3} md={3} role="listitem">
          {renderTopicCard(topic)}
        </Grid>
      ));
    };

    return (
      <div className="rc-OtherTopics" css={style}>
        {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
        <Typography className="other-topics-header" component="h2" variant="h2semibold">
          {_t('Other topics to explore')}
        </Typography>
        <Grid container role="list">
          {renderTopicCardList()}
        </Grid>
      </div>
    );
  }
}

type graphqlProps = {
  data?: {
    loading?: boolean;
    DomainsV1Resource?: { domains?: { elements?: Array<Subdomain> } };
  };
};

const DomainGraphqlOptions = {
  options: () => {
    return {
      ...apolloFetchPolicy({ cacheForLoggedOutOnly: false }),
      errorPolicy: 'all' as const,
    };
  },
  props: ({ data }: graphqlProps) => ({
    topicsList: (!data?.loading && data?.DomainsV1Resource?.domains?.elements) || [],
    loading: data?.loading,
  }),
};

export default compose<Props, {}>(
  connectToRouter<Props, {}>((router) => ({ router })),
  graphql(DomainGetAllQuery, DomainGraphqlOptions),
  setDisplayName('OtherTopicsHOC')
)(OtherTopics);
